import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useNavigate } from 'react-router-dom'
import PasswordInput from '../components/PasswordInput'

const FP = "'Playfair Display', Georgia, serif"
const FB = "'DM Sans', system-ui, sans-serif"

export default function ResetPassword() {
  const navigate = useNavigate()
  const [password, setPassword]   = useState('')
  const [confirm, setConfirm]     = useState('')
  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState(null)
  const [success, setSuccess]     = useState(false)
  const [ready, setReady]         = useState(false)
  const [pendingCode, setPendingCode] = useState(null)
  const [exchanging, setExchanging]   = useState(false)

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('code')
    if (code) {
      setPendingCode(code)
    } else {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) setReady(true)
      })
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') setReady(true)
    })
    return () => subscription.unsubscribe()
  }, [])

  async function confirmReset() {
    if (!pendingCode || exchanging) return
    setExchanging(true)
    setError(null)
    const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(pendingCode)
    setExchanging(false)
    if (exchangeError) {
      setError('El link ha expirado o ya fue usado. Solicita uno nuevo desde la pantalla de inicio de sesión.')
      setPendingCode(null)
      return
    }
    setReady(true)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    if (password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres.'); return }
    if (password !== confirm) { setError('Las contraseñas no coinciden.'); return }
    setLoading(true)
    const { error: updateError } = await supabase.auth.updateUser({ password })
    if (updateError) {
      setError('No se pudo actualizar la contraseña. El link puede haber expirado.')
      setLoading(false)
      return
    }
    setSuccess(true)
    setLoading(false)
    setTimeout(() => navigate('/login', { replace: true }), 2000)
  }

  return (
    <div style={S.container}>
      <div style={S.card}>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img src="/logo-v1.png" alt="Bellason" width={32} height={32} style={{ display: 'inline-block', marginBottom: '12px', objectFit: 'contain' }} />
          <h1 style={S.title}>Bellason</h1>
          <p style={S.subtitle}>Nueva contraseña</p>
        </div>

        {success ? (
          <div style={S.successBox}>
            <p style={S.successText}>Contraseña actualizada. Redirigiendo…</p>
          </div>

        ) : !ready && pendingCode ? (
          <div style={{ textAlign: 'center' }}>
            <p style={S.waitSub}>Confirma que eres tú quien solicitó el cambio de contraseña.</p>
            {error && <p style={S.error}>⚠ {error}</p>}
            <button type="button" onClick={confirmReset} disabled={exchanging} style={{ ...S.btnPrimary, opacity: exchanging ? 0.6 : 1, cursor: exchanging ? 'not-allowed' : 'pointer', marginTop: '12px' }}>
              {exchanging ? 'Verificando…' : 'Continuar'}
            </button>
          </div>

        ) : !ready ? (
          <div style={{ textAlign: 'center' }}>
            <p style={S.waitSub}>Verificando link…</p>
            <p style={{ ...S.waitSub, marginBottom: '1.5rem' }}>Si esto tarda, el link puede haber expirado.</p>
            {error && <p style={S.error}>⚠ {error}</p>}
            <button type="button" style={S.btnGhost} onClick={() => navigate('/login')}>
              Volver al login
            </button>
          </div>

        ) : (
          <form onSubmit={handleSubmit} style={S.form}>
            <PasswordInput
              style={S.input}
              placeholder="Nueva contraseña"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
            />
            <PasswordInput
              style={S.input}
              placeholder="Confirmar contraseña"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
            />
            {error && <p style={S.error}>⚠ {error}</p>}
            <button type="submit" disabled={loading} style={{ ...S.btnPrimary, opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Guardando…' : 'Guardar contraseña'}
            </button>
          </form>
        )}

      </div>
    </div>
  )
}

const S = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--bln-bg)',
    padding: '1.5rem 1rem',
    fontFamily: FB,
  },
  card: {
    backgroundColor: 'var(--bln-bg)',
    padding: '2.5rem 2rem',
    borderRadius: '14px',
    width: '100%',
    maxWidth: '400px',
    border: '1px solid var(--bln-accent-tint)',
  },
  title: {
    fontFamily: FP,
    color: 'var(--bln-text)',
    fontSize: '1.8rem',
    fontWeight: 400,
    textAlign: 'center',
    margin: '0 0 4px',
    letterSpacing: '-0.3px',
  },
  subtitle: {
    color: 'var(--bln-text-muted)',
    textAlign: 'center',
    margin: 0,
    fontSize: '13px',
    fontWeight: 300,
    lineHeight: 1.6,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  input: {
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid var(--bln-accent-tint)',
    backgroundColor: 'transparent',
    color: 'var(--bln-text)',
    fontSize: '14px',
    fontFamily: FB,
    fontWeight: 300,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  btnPrimary: {
    width: '100%',
    padding: '13px',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--bln-accent)',
    color: '#fff',
    fontWeight: 500,
    fontSize: '14px',
    fontFamily: FB,
    cursor: 'pointer',
    marginTop: '4px',
    letterSpacing: '0.2px',
  },
  btnGhost: {
    background: 'none',
    border: '1px solid var(--bln-accent-tint)',
    borderRadius: '8px',
    color: 'var(--bln-accent)',
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: FB,
    fontWeight: 300,
  },
  error: {
    color: 'var(--bln-danger)',
    fontSize: '13px',
    margin: 0,
    padding: '8px 12px',
    background: 'rgba(248,113,113,0.06)',
    borderRadius: '8px',
    border: '1px solid rgba(248,113,113,0.15)',
    fontWeight: 300,
  },
  successBox: {
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid var(--bln-accent-tint)',
    background: 'rgba(168,85,247,0.04)',
    textAlign: 'center',
  },
  successText: {
    color: 'var(--bln-accent)',
    margin: 0,
    fontSize: '14px',
    fontFamily: FP,
    fontStyle: 'italic',
    fontWeight: 400,
  },
  waitSub: {
    color: 'var(--bln-text-muted)',
    fontSize: '13px',
    fontWeight: 300,
    lineHeight: 1.6,
    margin: '0 0 8px',
  },
}